import { inject, Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { ExternalFormsService } from 'superlikers-microsites-api'

import { User } from '@core/data/models'
import { LocalStorage } from '../local-storage/local-storage'

interface CacheItem<T> {
  data: T
  expiryTimestamp: number
  serverTimestamp: number
}

/**
 * CacheService - Smart cache with TTL, user switching detection, and server invalidation
 *
 * Features:
 * - TTL expiration (default 12 hours)
 * - Auto-clears on user switch
 * - Server-controlled cache invalidation
 */
@Injectable({
  providedIn: 'root'
})
export class CacheService {
  #externalFormsService = inject(ExternalFormsService)
  #localStorage = inject(LocalStorage)

  #ttlMinutes = signal(720) // 12 hours
  #serverTimestamp = signal<number | null>(null)
  #expiryTimestamp = signal(new Date().getTime() + this.#ttlMinutes() * 60 * 1000)
  #hasInitialized = signal(false)

  /**
   * Initialize cache validation.
   * Must be called after user authentication.
   * @param currentUser - Current authenticated user
   */
  async init(currentUser: User | null): Promise<void> {
    if (this.#hasInitialized()) return
    this.#hasInitialized.set(true)

    // Check if user has changed to clear cache data
    const cachedUser = this.#localStorage.getItem('cached_user')
    if (currentUser && cachedUser !== currentUser.uid) {
      this.clear()
      this.#localStorage.setItem('cached_user', currentUser.uid)
    }

    // Check if server timestamp has changed to clear cache data
    const newTimestamp = await this.#getServerTimestamp()
    if (!newTimestamp) return

    const storedTimestamp = this.#localStorage.getItem('cache_server_timestamp')

    if (storedTimestamp) {
      const oldTimestamp = parseInt(storedTimestamp)

      // If server timestamp is newer, clear all cache
      if (newTimestamp > oldTimestamp) {
        console.log('Server timestamp updated - clearing all cache')
        this.clear()
      }
    }

    // Store new timestamp and set signal
    this.#localStorage.setItem('cache_server_timestamp', newTimestamp.toString())
    this.#serverTimestamp.set(newTimestamp)
  }

  /**
   * Store data in cache with TTL and server timestamp.
   */
  setItem<T>(key: string, data: T): void {
    const serverTimestamp = this.#serverTimestamp()
    if (!serverTimestamp) return

    const cacheItem: CacheItem<T> = {
      data,
      expiryTimestamp: this.#expiryTimestamp(),
      serverTimestamp
    }

    this.#localStorage.setItem(key, JSON.stringify(cacheItem))
  }

  /**
   * Retrieve data from cache.
   * Returns null if expired or server timestamp changed.
   */
  getItem<T>(key: string): T | null {
    const item = this.#localStorage.getItem(key)

    if (!item) return null

    try {
      const cacheItem: CacheItem<T> = JSON.parse(item)

      const currentServerTime = this.#serverTimestamp()
      if (!currentServerTime) return null

      if (currentServerTime > cacheItem.serverTimestamp) {
        this.removeItem(key)
        return null
      }

      const now = new Date().getTime()
      if (now > cacheItem.expiryTimestamp) {
        this.removeItem(key)
        return null
      }

      return cacheItem.data
    } catch {
      this.removeItem(key)
      return null
    }
  }

  /**
   * Remove specific cache item.
   */
  removeItem(key: string): void {
    this.#localStorage.removeItem(key)
  }

  /**
   * Clear all cache.
   */
  clear(): void {
    this.#localStorage.clear()
  }

  /**
   * Fetch server timestamp from external form.
   * Used to detect when backend data structure changes.
   */
  async #getServerTimestamp(): Promise<number | null> {
    try {
      const list$ = this.#externalFormsService.getExternalsFormList({ page: 1, category: 'cache' })
      const list = await firstValueFrom(list$)

      if (list.data.length === 0) return null

      const fields$ = this.#externalFormsService.getExternalsFormFields(list.data[0]._id)
      const fields = await firstValueFrom(fields$)

      const field = fields.object.fields.find(field => field.name === 'time')?.default_value ?? ''

      const date = new Date(field)
      return date.getTime()
    } catch (error) {
      console.error('Failed to fetch server timestamp:', error)
      return null
    }
  }
}
